import React, { useState } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  DatePicker,
  InputNumber,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

const AddTreasuryBillContract = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [settlementOptions, setSettlementOptions] = useState([]);
  const [tradeOptions, setTradeOptions] = useState([]);
  const navigate = useNavigate();

  const onFinish = async (values) => {
    try {
      setIsLoading(true);
      const payload = {
        clientId: values.clientId,
        productId: values.productId,
        faceValue: values.faceValue,
        purchasePrice: values.purchasePrice,
        issueDate: values.issueDate?.format("YYYY-MM-DD"),
        maturityDate: values.maturityDate?.format("YYYY-MM-DD"),
        tenor: values.tenor,
        discountRate: values.discountRate,
        settlementId: values.settlementId,
        tradeId: values.tradeId,
      };
      // await BOM.SendReq(apiConfig.createTBContract, payload);
      console.log("AddTBContract payload:", payload);
      BOM.AlertMsg(cnt.SUCCESS, "Treasury Bill Contract created successfully.");
      navigate(-1);
    } catch (error) {
      BOM.AlertMsg(cnt.DANGER, error?.message || "Failed to create contract.");
    } finally {
      setIsLoading(false);
    }
  };

  const LabelWithTip = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  return (
    <MainLayout pageItems={staticConfig.newtreasuryBillContract}>
      <TopContent pageTitle={labelConfig.treasuryBillContract} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
              Create Treasury Bill Contract
            </h3>

            <Form layout="vertical" form={form} onFinish={onFinish}>
              {/* Client & Product */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Client"
                        tip="The client this contract belongs to."
                      />
                    }
                    name="clientId"
                    rules={[{ required: true, message: "Client is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={clientOptions}
                      placeholder="Select Client"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Product"
                        tip="The treasury product linked to this contract."
                      />
                    }
                    name="productId"
                    rules={[{ required: true, message: "Product is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={productOptions}
                      placeholder="Select Product"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Trade & Settlement */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Trade"
                        tip="The trade this contract is linked to."
                      />
                    }
                    name="tradeId"
                    rules={[{ required: true, message: "Trade is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={tradeOptions}
                      placeholder="Select Trade"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Settlement"
                        tip="The settlement record associated with this contract."
                      />
                    }
                    name="settlementId"
                    rules={[
                      { required: true, message: "Settlement is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={settlementOptions}
                      placeholder="Select Settlement"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Face Value & Purchase Price */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Face Value"
                        tip="The nominal / par value of the treasury bill."
                      />
                    }
                    name="faceValue"
                    rules={[
                      { required: true, message: "Face value is required" },
                      { type: "number", min: 1 },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 50,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Purchase Price"
                        tip="The actual price paid for the treasury bill."
                      />
                    }
                    name="purchasePrice"
                    rules={[
                      { required: true, message: "Purchase price is required" },
                      { type: "number", min: 1 },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 49,250,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Discount Rate & Tenor */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Discount Rate (%)"
                        tip="The annualised discount rate applied to this treasury bill."
                      />
                    }
                    name="discountRate"
                    rules={[
                      { required: true, message: "Discount rate is required" },
                      { type: "number", min: 0 },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 12.50"
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Tenor (Days)"
                        tip="The number of days from issue to maturity."
                      />
                    }
                    name="tenor"
                    rules={[
                      { required: true, message: "Tenor is required" },
                      { type: "number", min: 1 },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 91"
                      min={1}
                      precision={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Issue Date & Maturity Date */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Issue Date"
                        tip="The date the treasury bill is issued."
                      />
                    }
                    name="issueDate"
                    rules={[
                      { required: true, message: "Issue date is required" },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%", height: 44 }}
                      format="YYYY-MM-DD"
                      placeholder="YYYY-MM-DD"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <LabelWithTip
                        text="Maturity Date"
                        tip="The date the treasury bill matures."
                      />
                    }
                    name="maturityDate"
                    rules={[
                      { required: true, message: "Maturity date is required" },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%", height: 44 }}
                      format="YYYY-MM-DD"
                      placeholder="YYYY-MM-DD"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      marginRight: 12,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => form.submit()}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #BA24D5",
                      background: "#BA24D5",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Submit
                  </button>
                </Col>
              </Row>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({ rightBtn: state.RightSideBtn });
export default connect(mapStateToProps, { rightAction })(
  AddTreasuryBillContract,
);
